<template>
  <route-not-found v-if="global.xgc" />
  <v-container v-else class="pt-0">

    <error-dialog :status="loadStatus" />
    <error-dialog :status="addStatus" />

    <!-- WARNING: this dialog has A LOT of overlap with the giftcard-dialog component -->
    <dialog-scroll-lock
      v-if="product"
      width="400"
      :value="true"
      @input="$router.push($routeAssign({ params: {} }))"
    >
      <v-card>
        <v-btn class="float-right mt-3 mr-3" icon @click="$router.push($routeAssign({ params: {} }))">
          <v-icon v-html="mdiClose" />
        </v-btn>
        <v-card-title class="pa-4">Gift card</v-card-title>
        <v-card-subtitle class="px-4 mt-0">
          <v-card class="float-right ml-4 mb-1 gc-border" width="100">
            <v-img :aspect-ratio="337 / 213" :src="product.img" />
          </v-card>
          <div class="overline short" style="color: rgba(0, 0, 0, 0.87);" v-html="product.name" />
          <giftcard-details :src="product.src" :product-id="product.id" />
        </v-card-subtitle>
        <div style="clear: both;" />
        <div class="px-4" v-bind:class="{ 'pt-4': product.values.fixed, 'pb-4': !product.values.fixed }">
          <v-form v-model="amtValid" @submit.prevent="() => amtValid && add()">
            <giftcard-amount
              :cur="product.cur"
              :allowed-values="product.values"
              :max="maxValue"
              v-model="amt"
            />
          </v-form>
        </div>
        <template v-if="noAmtValid">
          <v-divider />
          <v-card-text class="error--text font-italic pa-4">Max gift card value is restricted to <currency :code="$options.filters.cur(country)" />{{maxValue}} – there are no valid values that can be assigned to this gift card. Please try a different one.</v-card-text>
        </template>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn class="mr-2" exact :to="$routeAssign({ params: {} })">Cancel</v-btn>
          <add-to-project-btn
            color="primary"
            :preview="preview"
            :disabled="!amtValid"
            :loading="addStatus.pending"
            @select="add"
          />
        </v-card-actions>
        <!--
        <template v-if="product.disclosure">
          <v-divider />
          <v-card-text class="pa-4 caption grey lighten-4 grey--text text--darken-1 no-margin-last-p" v-html="product.disclosure" />
        </template>
        -->
      </v-card>
    </dialog-scroll-lock>

    <giftcard-browser
      :search="search"
      :country="country"
      :loading-provider-id="loadStatus.pending ? providerId : null"
      @input-search="x => search = x"
      @input-country="x => country = x"
    />
  </v-container>
</template>


<style>
.no-margin-last-p > p:last-child {
  margin-bottom: 0;
}
</style>


<script>
import AddToProjectBtn from '@/components/AddToProjectBtn.vue';
import Currency from '@/components/Currency.vue';
import ErrorDialog from '@/components/ErrorDialog.vue';
import GiftcardAmount from '@/components/GiftcardAmount.vue';
import GiftcardBrowser from '@/components/GiftcardBrowser.vue';
import GiftcardDetails from '@/components/GiftcardDetails.vue';
import RouteNotFound from '@/views/RouteNotFound.vue';
import { orgSkin } from '@/mixins/org-skin.js';
import pendingOrders from '@/mixins/pending-orders.js';
import { cache, ssr } from '@/mixins/ssr.js';
import { publicDesign, blankDesign } from '@/mixins/public-design.js';
import { buildDefaultOrder, updateOrderDesign } from '@/utils/edit-order.js';
import { SUPPORTED_COUNTRY_CODES } from '@/utils/giftcard.js';
import { CURRENCY_CODES, COUNTRY_CODE } from '@/utils/intl.js';
import uuid4 from '@/utils/uuid.js';
import { mdiClose, mdiFileDocument, mdiInformation } from '@mdi/js';


function isPageEmptyOrOnlyGiftcards(page, designId) {
  for (const blob of page.blobs) {
    if (blob.type == 'GIFTCARD')
      continue;
    if (!blob.design_id || (blob.for_design_id == designId))
      return false;
  }
  return true;
}


function addBlobToOrder(blob, order) {
  let i = order.pages.slice(0, 2).findIndex(x => isPageEmptyOrOnlyGiftcards(x, order.design_id));
  if (i == -1) {
    // We could not find a page that's empty or just contains gift cards, splice a new page in after all the cover pages
    i = order.pages.findIndex(x => !x.blobs.some(y => y.for_design_id == order.design_id));
    i = (i == -1) ? order.pages.length : i;
    order.pages.splice(i, 0, { id: uuid4(), blobs: [] });
  }
  const page = order.pages[i];
  const existing = page.blobs.filter(x => x.type == 'GIFTCARD');
  while (existing.find(e => (blob.x == e.x) && (blob.y == e.y))) {
    blob.x += 50;
    blob.y += 50;
  }
  page.blobs.push(blob);
  return i;
}


export default {
  name: 'catalog-gift-cards',

  routerViewMeta: {
    $subtitle: 'Merchant gift cards',
    description: 'Browser gift cards for thousands of international merchants'
  },

  mixins: [ orgSkin, pendingOrders, publicDesign, ssr ],

  components: {
    AddToProjectBtn,
    Currency,
    ErrorDialog,
    GiftcardAmount,
    GiftcardBrowser,
    GiftcardDetails,
    RouteNotFound
  },

  props: [ 'providerId' ],

  data() {
    return {
      search: '',
      country: SUPPORTED_COUNTRY_CODES.includes(COUNTRY_CODE) ? COUNTRY_CODE : 'US',
      product: null,
      maxValue: 500000,
      amt: 0,
      amtValid: false,
      loadedProduct: false,
      addStatus: this.$track('add'),
      loadStatus: this.$track('load')
    };
  },

  computed: {
    noAmtValid() {
      if (!this.product)
        return false;
      const allowed = this.product.values;
      const min = allowed.range ? allowed.range.min : Math.min(...allowed.fixed);
      return this.maxValue < min;
    },

    preview() {
      if (!this.product)
        return null;
      const order = _.cloneDeep(blankDesign);
      order.pages[0].blobs.push(this.getBlob());
      return order;
    },

    routerViewMeta() {
      if (!this.product)
        return null;
      return {
        'url': `/gift-cards/${this.product.src}:${this.product.id}`,
        'title': 'Gift card: ' + this.product.name,
        'description': 'Send this group gift card via Ellacard',
        'image': this.product.img
      };
    },

    hydrated() {
      return !this.providerId || this.loadedProduct;
    }
  },

  created() {
    Object.assign(this, { mdiClose, mdiFileDocument, mdiInformation });
  },

  filters: {
    cur(country) {
      return CURRENCY_CODES[country];
    }
  },

  methods: {
    async load() {
      this.loadedProduct = false;
      this.product = null;
      this.amt = 0;
      this.amtValid = false;
      if (!this.providerId)
        return;
      const [ provider, id ] = this.providerId.split(':');
      try {
        this.product = await cache(this.$call)('get-giftcard', { provider, id });
        if (this.product.values.fixed)
          this.amt = this.product.values.fixed[0];
        else
          this.amt = this.product.values.range.min;
        if (!this.product.countries.includes(this.country))
          this.country = this.product.countries[0] || 'US';
      } catch (e) {
        // TODO: throw (from the backend) and catch the specific type of exception
        this.$router.push(this.$routeAssign({ params: {} }));
        throw new VisibleError('Gift card not found: ' + this.providerId);
      } finally {
        this.loadedProduct = true;
      }
    },

    getBlob() {
      const [ src, product_id ] = this.providerId.split(':');
      return {
        id: uuid4(),
        user_id: this.globalUserId || '-',
        x: 300,
        y: 420,
        w: 337,
        h: 212.5,
        r: 0,
        type: 'GIFTCARD',
        giftcard: {
          amt: parseInt(this.amt),
          cur: CURRENCY_CODES[this.country],
          country: this.country,
          src,
          product_id,
          product_img: this.product.img,
          product_name: this.product.name,
          allowed_values: this.product.values
        }
      };
    },

    async add(orderId, order) {
      const routeOnClose = this.$routeAssign({});

      if (!order) {
        order = buildDefaultOrder();
        updateOrderDesign(order, this.blankDesignId, blankDesign);
        addBlobToOrder(this.getBlob(), order);
        const params = { orderId, order, initDirty: true, routeOnClose };
        this.$router.push({ name: 'project', params, hash: '#type' });
        return;
      }

      if (order.type == 'INVITATION')
        throw new VisibleError('Gift cards can only be added to <i>personal</i> or <i>group</i> cards, not invitations');;

      const existing = [];
      for (const page of order.pages)
        for (const blob of page.blobs)
          if (blob.type == 'GIFTCARD')
            existing.push(blob.giftcard);

      if (existing.length && (this.country != existing[0].country))
        throw new VisibleError(`Cannot add gift cards from multiple countries to the same project. Please change your country selection to ${this.$options.filters['country-name'](existing[0].country)} or remove the existing gift card from the target project.`);

      const matching = existing.find(x => x.src + ':' + x.product_id == this.providerId);
      if (matching) {
        const r = await this.$confirm({
          title: 'Are you sure?',
          msg: `You already have a gift card for "${matching.product_name}" in this project. Are you sure you want to add an additional one?`,
          buttons: [ 'Cancel', 'Add' ],
          primary: 'Add'
        });
        if (r == 'Cancel')
          return;
      }

      if (order.design_id == this.nullCoverDesignId)
        order.design_id = this.blankDesignId;

      const i = addBlobToOrder(this.getBlob(), order);
      await this.updatePendingOrder({ order_id: orderId, order });

      this.$confirm({
        title: 'Gift card added',
        msg: `Your gift card has been added ${i ? 'to page 2 ' : ''}– you can resize it or drag it to other pages`,
        buttons: [ 'Ok' ],
        primary: 'Ok'
      });

      this.$router.push({ name: 'project', params: { orderId, routeOnClose }, hash: '#design' });
    }
  },

  watch: {
    providerId: {
      immediate: true,
      handler() {
        this.load();
      }
    },

    'global.profile.org_id': {
      immediate: true,
      async handler(v) {
        this.maxValue = v && (await this.getOrgSkin(v))?.max_giftcard_value || 500000;
      }
    }
  }
}
</script>
